/**
 * Pure ADR-MV-09 helpers — map_camera dismiss-until-Save + Save token resolve.
 * Kept free of React so G12/G14/G27 can unit-test without mounting chrome.
 */
import {
  normalizeMapViewBounds,
  type MapViewKind,
  type ShopMapBoundsOverlay,
} from '../shopMapViewModel.js';

export type MapCameraWizardStep = 'kind' | 'configure' | 'name' | 'confirm';

/**
 * below-sm + map_camera + configure → host Save CTA; wizard chrome dismissed.
 * Used for render decisions (G12: never paint sheet before capture) and smUp retoggle (G14).
 */
export function isMapCameraDismissUntilSave(args: {
  readonly open: boolean;
  readonly kind: MapViewKind | null;
  readonly step: MapCameraWizardStep;
  readonly smUp: boolean;
}): boolean {
  return (
    args.open &&
    args.kind === 'map_camera' &&
    args.step === 'configure' &&
    !args.smUp
  );
}

export type MapCameraSaveResolve =
  | {
      readonly ok: true;
      readonly bounds: ShopMapBoundsOverlay;
      readonly nextStep: 'name';
    }
  | {
      readonly ok: false;
      /** i18n key — caller translates for configureError banner. */
      readonly errorKey: 'directory.mapView.boundsInvalid';
    };

/**
 * Host Save CTA → capture current map bounds (G13 null-bounds stays on capture).
 * Success → reopen wizard at name; failure → keep dismiss-until-Save + surface error.
 */
export function resolveMapCameraSaveCapture(
  bounds: ShopMapBoundsOverlay | null,
): MapCameraSaveResolve {
  if (bounds === null) {
    return { ok: false, errorKey: 'directory.mapView.boundsInvalid' };
  }
  return {
    ok: true,
    bounds: normalizeMapViewBounds(bounds),
    nextStep: 'name',
  };
}

/**
 * After successful Save: leave dismiss-until-Save and land on name (G27 reopen).
 * After failed Save: stay dismissed and keep configure (G13).
 */
export function nextDismissUntilSaveAfterCapture(args: {
  readonly saveOk: boolean;
  readonly smUp: boolean;
}): { readonly captureActive: boolean; readonly step: 'configure' | 'name' } {
  if (args.saveOk) {
    return { captureActive: false, step: 'name' };
  }
  return {
    captureActive: !args.smUp,
    step: 'configure',
  };
}
